
import { useEffect, useMemo, useCallback } from 'react';
import { parseISO } from 'date-fns';
import { useTrainingJournal } from '@/features/training-journal';
import { TimelineMarker } from './types/insights';

import { Belt } from './types';
import { useSortedSessions } from './hooks/useSortedSessions';
import { useOfflineWeeklyInsights } from './hooks/useOfflineWeeklyInsights';
import { useTimelineDialogs } from './hooks/useTimelineDialogs';
import { TimelineDialogs } from './TimelineDialogs';
import { TimelineTickElements } from './TimelineTickElements';
import { TrainingSession } from '@/types/training';
import { WeeklyInsight } from './types/insights';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

interface MicroJournalTickMarksProps {
  startDate: Date;
  endDate: Date;
  belts: Belt[];
  baseBeltSpacing: number;
  stripeSpacings: number[];
}

export function MicroJournalTickMarks({
  belts,
  baseBeltSpacing,
  stripeSpacings
}: MicroJournalTickMarksProps) {
  const { sessions, refreshSessions, isLoading: sessionsLoading, error: sessionsError } = useTrainingJournal();
  const { weeklyInsights, fetchWeeklyInsights, isLoading: insightsLoading, error: insightsError } = useOfflineWeeklyInsights();
  const sortedSessionsForDialogs = useSortedSessions(sessions);

  const {
    selectedSession,
    setSelectedSession,
    selectedInsight,
    setSelectedInsight,
    isSessionDialogOpen,
    setIsSessionDialogOpen,
    isInsightDialogOpen,
    setIsInsightDialogOpen,
    currentIndex,
    setCurrentIndex,
    handleOpenChange,
    handleInsightOpenChange,
    handleNavigate
  } = useTimelineDialogs();

  // Create simple markers without grouping or consolidation
  const markers = useMemo(() => {
    const newMarkers: TimelineMarker[] = [];

    // Process training sessions - each session gets its own marker
    if (Array.isArray(sessions)) {
      sessions.forEach(session => {
        if (session && session.date && session.beltRank) {
          newMarkers.push({
            type: 'session',
            date: parseISO(session.date),
            data: session
          });
        }
      });
    }

    // Process weekly insights - each insight gets its own marker
    if (Array.isArray(weeklyInsights)) {
      weeklyInsights.forEach((insight) => {
        if (!insight || !insight.week_start) return;

        // Determine belt rank and stripe count from sessions in that week
        let beltRank = 'white';
        let stripeCount = '0';

        if (Array.isArray(sessions)) {
          const sessionsInWeek = sessions.filter(s => {
            if (!s || !s.date) return false;
            const sessionDate = parseISO(s.date);
            const insightDate = parseISO(insight.week_start);
            const weekEndDate = new Date(insightDate.getTime() + 7 * 24 * 60 * 60 * 1000);
            return sessionDate >= insightDate && sessionDate <= weekEndDate;
          });

          if (sessionsInWeek.length > 0) {
            const mostRecent = sessionsInWeek.sort((a, b) =>
              parseISO(b.date).getTime() - parseISO(a.date).getTime()
            )[0];
            beltRank = mostRecent.beltRank || beltRank;
            stripeCount = String(mostRecent.stripeCount || stripeCount);
          }
        }

        const insightWithBelt: WeeklyInsight = {
          ...insight,
          belt_rank: beltRank,
          stripe_count: stripeCount,
          technique_tags: Array.isArray(insight.technique_tags) ?
            insight.technique_tags :
            (insight.technique_tags ? [insight.technique_tags as unknown as string] : [])
        };

        try {
          const parsedDate = parseISO(insight.week_start);
          newMarkers.push({
            type: 'insight',
            date: parsedDate,
            data: insightWithBelt
          });
        } catch (dateError) {
          console.error(`Error parsing date for insight ${insight.week_start}:`, dateError);
        }
      });
    }

    // Sort markers by date (oldest to newest)
    return newMarkers.sort((a, b) => {
      if (!a.date || !b.date) return 0;
      return a.date.getTime() - b.date.getTime();
    });
  }, [sessions, weeklyInsights]);

  // Create simple marker groups by belt/stripe for positioning
  const markerGroups = useMemo(() => {
    const groups: Record<string, TimelineMarker[]> = {};

    markers.forEach(marker => {
      let beltRank = 'white';
      let stripeCount = '0';

      if (marker.type === 'session') {
        const session = marker.data as TrainingSession;
        beltRank = session?.beltRank || 'white';
        stripeCount = String(session?.stripeCount || '0');
      } else if (marker.type === 'insight') {
        const insight = marker.data as WeeklyInsight;
        beltRank = insight?.belt_rank || 'white';
        stripeCount = insight?.stripe_count || '0';
      }

      const key = `${beltRank.toLowerCase()}_${stripeCount}`;
      groups[key] = groups[key] || [];
      groups[key].push(marker);
    });

    return groups;
  }, [markers]);

  const isLoading = sessionsLoading || insightsLoading;
  const hasError = sessionsError || insightsError;

  useEffect(() => {
    // Only fetch data on mount, not on every dependency change
    // The hooks themselves handle their own data fetching
    if (!sessions && !sessionsLoading && !weeklyInsights && !insightsLoading) {
      refreshSessions();
      fetchWeeklyInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only on mount

  const handleMarkerClick = useCallback((marker: TimelineMarker) => {
    try {
      if (!marker) return;

      if (marker.type === 'session') {
        // Assert the data is a TrainingSession
        const session = marker.data as TrainingSession;
        if (!session) return;

        setSelectedSession(session);
        setIsSessionDialogOpen(true);

        // Safely find index with additional null checks
        if (Array.isArray(sortedSessionsForDialogs) && sortedSessionsForDialogs.length > 0) {
          const idx = sortedSessionsForDialogs.findIndex(s => s && s.id === session.id);
          setCurrentIndex(idx !== -1 ? idx : null);
        } else {
          setCurrentIndex(null);
        }
      } else if (marker.type === 'insight') {
        // Assert the data is a WeeklyInsight
        const insight = marker.data as WeeklyInsight;
        if (!insight) return;

        setSelectedInsight(insight);
        setIsInsightDialogOpen(true);
      }
    } catch (err) {
      console.error('Error handling marker click:', err);
    }
  }, [sortedSessionsForDialogs, setSelectedSession, setIsSessionDialogOpen, setCurrentIndex, setSelectedInsight, setIsInsightDialogOpen]);
  
  // Handler specifically for the navigation through sessions
  const handleDialogNavigate = (newIndex: number) => {
    if (Array.isArray(sortedSessionsForDialogs)) {
      handleNavigate(newIndex, sortedSessionsForDialogs);
    }
  };

  // Show error if any
  if (hasError) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          {sessionsError || insightsError || 'Error loading timeline data'}
        </AlertDescription>
      </Alert>
    );
  }

  // Show loading state
  if (isLoading) {
    return <div className="p-4 text-center text-muted-foreground">Loading timeline data...</div>;
  }

  return (
    <>
      <TimelineTickElements
        markerGroups={markerGroups}
        belts={belts}
        baseBeltSpacing={baseBeltSpacing}
        stripeSpacings={stripeSpacings}
        onMarkerClick={handleMarkerClick}
      />

      <TimelineDialogs
        selectedSession={selectedSession}
        selectedInsight={selectedInsight}
        isSessionDialogOpen={isSessionDialogOpen}
        isInsightDialogOpen={isInsightDialogOpen}
        sortedSessions={sortedSessionsForDialogs}
        currentIndex={currentIndex}
        onSessionDialogOpenChange={handleOpenChange}
        onNavigate={handleDialogNavigate}
        onInsightDialogOpenChange={handleInsightOpenChange}
      />
    </>
  );
}
